import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, EntitiesService, GenesisUnitOfWork, InvoiceService, ReportService, UtilityService } from 'app/core/core.module';
import { AssemblySchedule, Confirmation, Dealer, Invoice, Order, OrderLineDetail, OrderLineStatus, PackingSlip, Part } from 'app/core/model/entity-model';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { AllocateDialogComponent } from '../allocate-dialog/allocate-dialog.component';
import { OrderOptionDialogComponent } from '../order-option-dialog/order-option-dialog.component';
import { StatusUpdateDialogComponent } from '../status-update-dialog/status-update-dialog.component';
import { PackingSlipDialogComponent } from 'app/packing-slips/packing-slip-dialog/packing-slip-dialog.component';

@Component({
    selector: 'app-order-build-detail',
    templateUrl: './order-build-detail.component.html',
    styleUrls: ['./order-build-detail.component.scss'],
    standalone: false,
})
export class OrderBuildDetailComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    orderLineDetails: any;
    packingSlipId: number;
    buildScheduleIds: number[];
    invoiceId: number;
    orderId: any;
    orderLineId: any;

    filter: any;
    previousInvoice = '';
    showCompleted = false;
    showAsDialog = false;
    processing = false;
    canRemoveFromPackSlip = false;
    globalFilter: any;
    canDelete = false;
    canSetBuilt = false;
    canSetUnBuilt = false;
    canAllocateStock = false;
    canSeeDealerNotes = false;
    filterOutOptions = true;
    mode: string;
    storageKey = 'order-build-grid';
    title: string = 'Build Detail';
    DeliveryOptions = Order.DeliveryOptions;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onOptionChangedShared = SharedModule.onOptionChanged;

    constructor(
        private uow: GenesisUnitOfWork,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        private reportService: ReportService,
        private entitiesService: EntitiesService,
        private utility: UtilityService,
        private invoiceService: InvoiceService,
    ) {}

    get dealerDeliveryConfirmationEnabled(): boolean {
        return this.appInfo.getDealerDeliveryConfirmationEnabled();
    }

    async ngOnInit() {
        const deliveryOptions = this.uow.lookups.deliveryOptions;
        const dealers = await this.uow.dealers.all();

        // const orders = await this.uow.getOrders(); // 2/7/23 MR: commented out because of expand lower
        const users = await this.uow.users.all();
        this.canDelete = this.auth.hasRole('BuildDetail:CanDelete');
        this.canSetBuilt = this.auth.hasRole('BuildDetail:SetToBuilt');
        this.canSetUnBuilt = this.auth.hasRole('BuildDetail:SetToUnBuilt');
        this.canAllocateStock = this.auth.hasRole('BuildDetail:AllocateStock');
        this.canSeeDealerNotes = this.auth.hasRole('BuildDetail:CanSeeDealerNotes');
        await this.getData();
    }

    async getData() {
        // const orderLines = await this.uow.getActiveOrderLines();
        // const packinSlips = await this.uow.packingSlips.all(); // 2/7/23 MR: commented out because of expand lower

        let orderLineDetails = null;
        if (this.showAsDialog) {
            if (this.orderId) orderLineDetails = (await this.uow.getOrderBuildDetailByOrderId(this.orderId)) as OrderLineDetail[];
            else if (this.orderLineId) {
                orderLineDetails = await this.uow.getOrderLineDetailsByOrderLineId(this.orderLineId, [
                    'packingSlip',
                    'invoice',
                    'orderLine.order.achPayments',
                    'orderLine.order.confirmations',
                    'optionDetails.orderLine',
                    'notes',
                    'orderLine.order.type',
                ]);
            } else if (this.packingSlipId) {
                orderLineDetails = await this.uow.getOrderLineDetailsByPackSlipId(this.packingSlipId, [
                    'packingSlip',
                    'invoice',
                    'orderLine.order.achPayments',
                    'orderLine.order.confirmations',
                    'optionDetails.orderLine',
                    'notes',
                    'orderLine.order.type',
                ]);
            } else if (this.buildScheduleIds?.length > 0) {
                orderLineDetails = (await this.uow.orderLineDetails.byIds(
                    this.buildScheduleIds,
                    'packingSlip,invoice,orderLine.order.achPayments,orderLine.order.confirmations,optionDetails.orderLine,notes,orderLine.order.type',
                )) as OrderLineDetail[];
            } else if (this.invoiceId) {
                const invoice = (await this.uow.invoices.byId(this.invoiceId)) as Invoice;
                const dealerId = invoice.dealerId;
                orderLineDetails = (await this.uow.getUnInvoicedOrderLineDetailsByDealerId(dealerId, [
                    'packingSlip',
                    'invoice',
                    'orderLine.order.achPayments',
                    'orderLine.order.confirmations',
                    'optionDetails.orderLine',
                    'notes',
                    'orderLine.order.type',
                ])) as OrderLineDetail[];
                this.filterOutOptions = false;
            }
            if (this.filterOutOptions) orderLineDetails = orderLineDetails.filter((detail) => !detail.isOption);
        } else {
            orderLineDetails = (await this.uow.getOrderBuildDetail([
                'packingSlip',
                'invoice',
                'orderLine.order.achPayments',
                'orderLine.order.confirmations',
                'optionDetails.orderLine',
                'notes',
                'orderLine.order.type',
            ])) as OrderLineDetail[];
        }

        await this.entitiesService.setShipAddressNotes(orderLineDetails);
        this.orderLineDetails = orderLineDetails;

        // noteHTML
        this.entitiesService.setNoteHtml(this.orderLineDetails);
        // optionHTML
        this.entitiesService.setOptionHtml(this.orderLineDetails);

        // confirmation
        this.orderLineDetails.forEach((detail) => {
            if (!detail.orderLine.order.confirmations) return;

            const confirmations = detail.orderLine.order.confirmations.filter(
                (confirmation: Confirmation) => !confirmation.salesRepConfirmation && !confirmation.deleted && confirmation.approved,
            );
            if (confirmations && confirmations.length > 0) {
                const approvedDate = _(confirmations).orderBy('created', 'desc').valueOf()[0].approved;
                detail.confirmationApproved = approvedDate;
            }
        });
    }

    async refreshNotes() {
        const notes = await this.uow.getActiveOrderLineDetailNotes();
        this.orderLineDetails.forEach((detail) => {
            let rowNote = '';
            if (!!detail.notes && detail.notes.length > 0) {
                detail.notes.forEach((note) => {
                    rowNote += `${note ? `${note.dateDisplay} by ${note.createdBy.userName}: ${note.note}` : ''}<br>`;
                });
            }
            detail.noteHTML = `${rowNote}`;
            // detail.vinEndingDisplay = `${!!detail.vIN ? detail.vIN.substring(detail.vIN.length - 7) : ''}`;
        });
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    async save() {
        await this.uow.commit();
    }

    async saveDialog() {
        await this.dataGrid.instance.saveEditData();
        if (this.mode !== 'invoice' && this.mode !== 'split-line') {
            // check for duplicates in the same order
            const vins = this.orderLineDetails
                .filter((x) => {
                    return x.vIN !== null && x.deleted === null;
                })
                .map((x) => {
                    return x.vIN;
                })
                .filter((x) => {
                    return x.length > 0;
                });
            // console.log('vins', vins);

            const duplicateVins = vins.filter((e, i, a) => a.indexOf(e) !== i);
            if (duplicateVins.length !== 0) {
                await alert(`There are duplicate VINs on the Sales Order\r\n${duplicateVins}`, `${this.appInfo.settings.company}`);
                return;
            }

            if (vins.length !== 0) {
                // check for duplicate VINs on existing orders
                const vinIds = this.orderLineDetails
                    .filter((x) => {
                        return x.deleted === null;
                    })
                    .map((x) => {
                        return x.id;
                    });
                const duplicates = (await this.utility.duplicateVins(vins, vinIds)) as any;
                // console.log('duplicates', duplicates);
                const count = duplicates.length;
                if (count !== 0) {
                    await alert(
                        `The ${count > 1 ? 'VINs ' + duplicates + ' are' : 'VIN - ' + duplicates + ' - is'} used on an existing Sales Order`,
                        `${this.appInfo.settings.company}`,
                    );
                    return;
                }
            }
            await this.save();
            this.closeDialog();
        } else {
            const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
            if (!selectedRow) return;
            this.result.next({ success: true, orderLineDetailId: selectedRow.id });
        }
    }

    closeDialog() {
        if (this.hasChanges()) {
            this.dataGrid.instance.cancelEditData();
            this.uow.rollback();
        }
        if (this.showAsDialog) this.result.next({});
    }

    async onRowUpdating(e) {
        this.processing = true;
    }
    async onRowUpdated(e) {
        this.processing = false;
    }

    async rowPrepare(e) {
        if (e.rowType === 'data' && !!e.data.orderLine && !!e.data.orderLine.order && !!e.data.orderLine.order.dealer) {
            if (e.data.orderLine.order.dealer.colorCode === 'blue') {
                e.rowElement.className = e.rowElement.className + ' blue-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'red') {
                e.rowElement.className = e.rowElement.className + ' red-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'green') {
                e.rowElement.className = e.rowElement.className + ' green-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'orange') {
                e.rowElement.className = e.rowElement.className + ' orange-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'white') {
                e.rowElement.className = e.rowElement.className + ' white-line';
            } else if (e.data.orderLine.order.dealer.colorCode === 'gray') {
                e.rowElement.className = e.rowElement.className + ' gray-line';
            }
        }
    }

    getHTMLOptions(cellInfo) {
        if (!cellInfo || !cellInfo.options || !cellInfo.options.includes('<p>')) return;
        let rows = '';
        const data = cellInfo.options.split('<p>');
        data.forEach((field) => {
            rows += `${field.indexOf('-DELETED') !== -1 ? '<s><font color="red">' : ''}${field || ''}${field.indexOf('-DELETED') !== -1 ? '</s></font color="red">' : ''}<br>`;
        });
        return `${rows}`;
    }

    async printPDF(cell: any) {
        if (cell.column.dataField === 'packingSlip.packingSlipNumber') {
            const reportGuid = ((await this.uow.getPackSlipByNumber(cell.value)) as PackingSlip).reportGuid;
            this.reportService.openPackingSlip([reportGuid]);
        }
        if (cell.column.dataField === 'packingSlip.invoiceNumber') {
            const reportGuid = ((await this.uow.getPackSlipByInvoiceNumber(cell.value)) as PackingSlip).reportGuid;
            this.reportService.openInvoiceReport([reportGuid]);
        }
    }

    async addToPackSlip() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        const existingPackSlips = await this.uow.getDistinctPackSlips();
        const dialogValueArray = existingPackSlips.map((x) => {
            return {
                id: x.packingSlipNumber,
                name: `${x.packingSlipNumber} - ${x.dealer_CompanyName}`,
            };
        });

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add To Packing Slip',
                dialogPrompt: 'Enter Slip Number',
                checkNumeric: true,
                checkInteger: true,
                mode: 'selectWithId',
                dialogValueArray: dialogValueArray,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                const packSlip = await this.uow.getPackSlipByNumber(result.value);
                if (!packSlip) {
                    await alert(`Packing Slip # ${result.value} does not exist`, `${this.appInfo.settings.company}`);
                    return;
                }
                this.appInfo.pleaseWaitShow();
                const packSlipDeliveryOption = this.uow.lookups.deliveryOptions?.find((d: any) => d.name === packSlip.deliveryMethod);
                let detailIds = [];
                selectedRows.forEach((row) => {
                    row.statusId = OrderLineDetail.Statuses.PackSlipCreated;
                    row.packingSlipId = packSlip.id;
                    if (packSlipDeliveryOption) row.deliveryOptionId = packSlipDeliveryOption.id;
                    detailIds.push(row.id);
                    row.optionDetails.forEach((option) => {
                        option.statusId = OrderLineDetail.Statuses.PackSlipCreated;
                        option.packingSlipId = packSlip.id;
                        if (packSlipDeliveryOption) option.deliveryOptionId = packSlipDeliveryOption.id;
                        detailIds.push(option.id);
                    });
                    this.entitiesService.createOrderLineDetailNote(row.id, `Added to pack slip #${result.value}`, this.auth.currentUser.id);
                });
                await this.save();
                await this.refreshNotes();
                await this.utility.setPaymentLinePackSlipId(detailIds, packSlip.id);
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();
            }
            modalRef.hide();
        });
    }

    async removeFromPackSlip() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        await this.invoiceService.removeFromPackSlip(selectedRows);
        await this.refreshNotes();
        this.dataGrid.instance.clearSelection();
    }

    async printPackList() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        if (
            selectedRows.filter((x) => {
                return x.statusId === OrderLineDetail.Statuses.New;
            }).length !== 0
        ) {
            const result = await confirm(`One or more of the selected trailers is on status NEW. Continue?`, `${this.appInfo.settings.company}`);
            if (!result) return;
        }

        const shipToCount = _.uniq(
            selectedRows.map((x) => {
                return x.orderLine.order.shipToAddress1;
            }),
        ).length;
        if (shipToCount > 1) {
            await alert('There are multiple shipTo addresses selected', `${this.appInfo.settings.company}`);
            return;
        }

        const packSlips = _.uniq(
            selectedRows.map((x) => {
                return x.packingSlipId;
            }),
        );
        if (packSlips.length > 1) {
            await alert('There are multiple packing slips selected', `${this.appInfo.settings.company}`);
            return;
        }

        const dealer = selectedRows[0].orderLine.order.dealer;
        const freightCheck = selectedRows[0].deliveryOptionId === Order.DeliveryOptions.UPS;
        const defaultFreight = !!freightCheck ? selectedRows[0].orderLine.order.freight : 0;
        const notes = selectedRows[0].orderLine.order.shippingNote;
        if (packSlips.length === 1 && !packSlips[0]) {
            const modalRef = this.modalService.show(PackingSlipDialogComponent, {
                class: 'modal-sm',
                ignoreBackdropClick: true,
                keyboard: false,
                initialState: {
                    freight: defaultFreight,
                    notes: notes,
                    dealer: dealer,
                    selectedRows: selectedRows,
                    mode: 'edit',
                },
            });

            modalRef.content.result.subscribe(async (result) => {
                if (result.success) {
                    this.appInfo.pleaseWaitShow();
                    const packSlipDeliveryOption = this.uow.lookups.deliveryOptions?.find((d: any) => d.name === result.packingSlip.deliveryMethod);
                    selectedRows.forEach((row) => {
                        row.packingSlipId = result.packingSlip.id;
                        row.statusId = OrderLineDetail.Statuses.PackSlipCreated;
                        if (packSlipDeliveryOption) row.deliveryOptionId = packSlipDeliveryOption.id;
                        row.optionDetails.forEach((option) => {
                            option.packingSlipId = result.packingSlip.id;
                            option.statusId = OrderLineDetail.Statuses.PackSlipCreated;
                            if (packSlipDeliveryOption) option.deliveryOptionId = packSlipDeliveryOption.id;
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Created pack slip #${result.packingSlip.packingSlipNumber}`, this.auth.currentUser.id);
                    });
                    await this.uow.commit();
                    this.reportService.openPackingSlip([result.packingSlip.reportGuid]);
                    await this.getData();
                    this.appInfo.pleaseWaitHide();
                }
                modalRef.hide();
            });
        } else {
            const packingSlipIds = selectedRows.map((x) => x.packingSlipId);
            const reportGuids = await this.uow.getReportGuidsByInvoiceIds(packingSlipIds);
            this.reportService.openPackingSlip(reportGuids);
        }
    }

    async addNoteButtonClick(e) {
        if (!e) return;
        switch (e.itemData) {
            case 'Add Note':
                await this.addNote();
                break;
            case 'Add Detail Specific Note':
                await this.addNote(true);
                break;
        }
    }

    async addNote(detailSpecific?: boolean) {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows || selectedRows.length === 0) {
            await alert(`No rows selected`, `${this.appInfo.settings.company}`);
            return;
        }

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Build Note',
                dialogPrompt: 'Enter Note',
                dialogValue: detailSpecific ? selectedRows[0].note : null,
                checkNumeric: false,
                checkInteger: false,
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                this.appInfo.pleaseWaitShow();
                if (detailSpecific) {
                    selectedRows.forEach((row) => {
                        row.note = result.value;
                    });
                }
                await this.entitiesService.addNote(selectedRows, detailSpecific ? `Detail Specific Note: ${result.value}` : result.value, true);
                await this.save();
                await this.getData();
                this.appInfo.pleaseWaitHide();
                this.dataGrid.instance.clearSelection();
            }
            modalRef.hide();
        });
    }

    async multipleOrderLineQuantityCheck(orderLineDetailId: number): Promise<any> {
        return await this.utility.multipleOrderLineQuantityCheck(orderLineDetailId);
    }

    async splitOrderLines(orderLineDetailId: number): Promise<boolean> {
        const confirmSplit = await confirm(
            `The selected OrderLineDetail is part of a multi-quantity OrderLine.  Adding or deleting options will split this detail to its own OrderLine.
            Continue?

        If you wish to globally add or remove an option from the entire OrderLine, you can do so from the Order Detail screen.
        `,
            `${this.appInfo.settings.company}`,
        );

        if (!confirmSplit) return false;
        else {
            this.appInfo.pleaseWaitShow();
            const splitResult = await this.utility.splitOrderLinesBasedOnDetailEdit(orderLineDetailId);
            this.appInfo.pleaseWaitHide();
            return splitResult;
        }
    }

    async splitOrderLine() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        this.appInfo.pleaseWaitShow();
        const multiple = await this.multipleOrderLineQuantityCheck(selectedRow.id);

        if (multiple.orderLineQtyGreaterThanOne && !multiple.hasNotes && !multiple.hasNegativeChildren) {
            this.appInfo.pleaseWaitHide();
            const confirmSplit = await confirm(
                `The selected OrderLineDetail is part of a multi-quantity OrderLine.  Adding or deleting options will split this detail to its own OrderLine.
            Continue?

        If you wish to globally add or remove an option from the entire OrderLine, you can do so from the Order Detail screen.
        `,
                `${this.appInfo.settings.company}`,
            );

            if (!confirmSplit) return false;
            else {
                this.appInfo.pleaseWaitShow();
                const splitResult = await this.utility.splitOrderLinesBasedOnDetailEdit(selectedRow.id);
                await alert(`The OrderLines have been split.  You may now edit options from the Sales Order screen.`, `${this.appInfo.settings.company}`);
                this.appInfo.pleaseWaitHide();
                return splitResult;
            }
        } else {
            this.appInfo.pleaseWaitHide();
            await alert(`The selected OrderLineDetail has no siblings.  You may edit the options from the Sales Order screen.`, `${this.appInfo.settings.company}`);
        }
        this.appInfo.pleaseWaitHide();
    }

    async editOptions() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];

        //check if multiple OrderLine or single
        const multiple = await this.multipleOrderLineQuantityCheck(selectedRow.id);
        let oldEditSalesOrder = false;
        if (multiple.orderLineQtyGreaterThanOne && !multiple.hasNotes && !multiple.hasNegativeChildren) {
            //if multiple - ask to split it
            const splitCheck = await this.splitOrderLines(selectedRow.id);
            if (!splitCheck) return;
            await alert(`The OrderLines have been split.  You may now edit options`, `${this.appInfo.settings.company}`);
            // this.dataGrid.instance.clearSelection();
            await this.getData();
            return;
        } else if (multiple.orderLineQtyGreaterThanOne && (multiple.hasNotes || multiple.hasNegativeChildren)) {
            //a Sales Order that was edited by the old code
            await alert(
                `The selected OrderLineDetail belongs to an OrderLine that was already modified by the old program, either by itself or one of its siblings. `,
                `${this.appInfo.settings.company}`,
            );
            oldEditSalesOrder = true;
        }

        // console.log('selectedRow', selectedRow);
        const options = await this.uow.getOptionsForOrderLineDetail(selectedRow.id);
        // console.log('options', options);
        const availableOptions = ((await this.uow.getPartOptionsByPartId(selectedRow.orderLine.partId)) as any).map((x) => {
            return x.optionPart;
        });
        // console.log('available options', availableOptions);
        const modalRef = this.modalService.show(OrderOptionDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                options: options,
                availableOptions: availableOptions,
                orderLineDetail: selectedRow,
                deleteMode: oldEditSalesOrder ? 'old' : null,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async editPackingSlip(orderLineDetail: OrderLineDetail) {
        if (!orderLineDetail || !orderLineDetail.packingSlip) return;

        const modalRef = this.modalService.show(PackingSlipDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                packingSlip: orderLineDetail.packingSlip,
                order: orderLineDetail.orderLine.order,
                mode: 'edit',
            },
        });
        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async getMiscDescription(): Promise<any> {
        this.appInfo.pleaseWaitHide();
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Add Miscellaneous Description',
                dialogPrompt: 'Enter Description',
                checkNumeric: false,
                checkInteger: false,
                mode: 'text',
            },
        });

        const promise = new Promise<any>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.result && !!result.value) {
                    resolve(result.value);
                }
                modalRef.hide();
                this.appInfo.pleaseWaitShow();
                resolve(null);
            });
        });
        return promise;
    }

    selectionChange(e) {
        const selectedRowData = this.dataGrid.instance.getSelectedRowsData();
        this.canRemoveFromPackSlip = selectedRowData.length > 0 && selectedRowData.every((a) => a.packingSlipId != null);
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    async open(orderId: number) {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRow.length === 0) return;
        this.router.navigate(['/sales-orders/' + (!!selectedRow ? selectedRow[0].orderLine.order.id : 'new')]);
    }

    async rowDoubleClick(e) {
        if (!e.data || !e.data.id) return;
        await this.open(e.data.id);
    }

    async bulkUpdate() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        if (selectedRows.length > 1) {
            const success = await confirm(`Are you sure you want to bulk update the following?<br/><br/>${this.getDisplayNames(selectedRows)}`, this.appInfo.settings.company);
            if (!success) return;
        }

        const modalRef = this.modalService.show(StatusUpdateDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {},
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                for (let index = 0; index < selectedRows.length; index++) {
                    const row = selectedRows[index];

                    //UPDATING STATUS
                    if (result.statusId && row.statusId !== result.statusId) {
                        row.statusId = result.statusId;
                        if (result.statusId === OrderLineDetail.Statuses.DeliveredPickedUp && !row.delivered) row.delivered = new Date();
                        let status = null;
                        Object.keys(OrderLineDetail.Statuses).forEach((key) => {
                            if (OrderLineDetail.Statuses[key] === result.statusId) {
                                status = key;
                            }
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Status Changed to ${status}`, this.auth.currentUser.id);
                        row.optionDetails.forEach((option) => {
                            option.statusId = result.statusId;
                            if (result.statusId === OrderLineDetail.Statuses.DeliveredPickedUp && !option.delivered) option.delivered = new Date();
                        });
                    }

                    //UPDATING DUE DATE
                    if (result.dueDate && this.auth.hasRole('BuildDetail:CanUpdateShipDue')) {
                        row.dueDate = result.dueDate;

                        // const buildDue = moment(result.dueDate)
                        //     .add(this.uow.lookups.buildLeadTime * -1, 'days')
                        //     .toDate();
                        // row.buildDue = buildDue;
                        row.optionDetails.forEach((option) => {
                            option.dueDate = result.dueDate;
                            // option.dueDate = buildDue;
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Due Date Changed to ${moment(result.dueDate).format('MM/DD/YYYY')}`, this.auth.currentUser.id);
                    }

                    if (result.buildDue && this.auth.hasRole('BuildDetail:CanUpdateBuildDue')) {
                        row.buildDue = result.buildDue;
                        row.optionDetails.forEach((option) => {
                            option.buildDue = result.buildDue;
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Build Due Changed to ${moment(result.buildDue).format('MM/DD/YYYY')}`, this.auth.currentUser.id);
                    }

                    //UPDATING DELIVERY OPTIONS
                    if (result.delivery && row.deliveryOptionId !== result.delivery.id) {
                        row.deliveryOptionId = result.delivery.id;
                        row.optionDetails.forEach((option) => {
                            option.deliveryOptionId = result.delivery.id;
                        });
                        this.entitiesService.createOrderLineDetailNote(row.id, `Delivery Changed to ${result.delivery.name}`, this.auth.currentUser.id);
                    }

                    //UPDATING NOTES
                    if (result.noteValue) {
                        this.entitiesService.createOrderLineDetailNote(row.id, result.noteValue, this.auth.currentUser.id, 2, true);
                    }
                }
                await this.uow.commit();

                // check if the whole order is complete and complete it if it isn't
                if (result.value === OrderLineDetail.Statuses.Complete) {
                    selectedRows.forEach(async (row) => {
                        await this.utility.setOrderComplete([row.orderLine.orderId]);
                    });
                }

                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    getDisplayNames(buildDetails: OrderLineDetail[]) {
        if (!buildDetails) return '';
        return buildDetails.map((detail) => `${detail.orderLine?.order?.dealer?.companyName} - ${detail.vIN ? detail.vIN : detail.orderLine?.partNumber}`).join('<br/>');
    }

    async delete() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        if (!selectedRow.built) {
            await alert(`The selected detail has not been built.  Edit the Sales Order to remove the detail.`, this.appInfo.settings.company);
            return;
        }

        this.appInfo.pleaseWaitShow();
        const multiple = await this.multipleOrderLineQuantityCheck(selectedRow.id);
        if (multiple.orderLineQtyGreaterThanOne && !multiple.hasNotes && !multiple.hasNegativeChildren) {
            this.appInfo.pleaseWaitHide();
            await alert(`The selected OrderLineDetail has siblings.  Line must be split on the Sales Order.  `, `${this.appInfo.settings.company}`);
            return;
        }

        let message = `Are you sure you want to DELETE this detail?.`;
        if (selectedRow.built) message += `\r\n\r\nDeleting this detail will place all parts used to make it and its options back into inventory. Continue?`;
        this.appInfo.pleaseWaitHide();
        const result = await (confirm(`${message}`, `${this.appInfo.settings.company}`) as Promise<boolean>);
        if (!result) {
            return;
        }

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Reason for Deleting',
                dialogPrompt: 'Enter the reason you are deleting this trailer',
                checkNumeric: false,
                checkInteger: false,
                mode: 'text',
            },
        });

        const promise = new Promise<boolean>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                modalRef.hide();
                if (result.result) {
                    if (!result.value) {
                        alert('A reason is required in order to delete a trailer', this.appInfo.settings.company);
                        resolve(false);
                        return;
                    }

                    await this.entitiesService.addNote([selectedRow], `Delete Reason: ${result.value}`, true);
                    resolve(true);
                }
            });
        });
        const success = await promise;
        if (!success) return;

        this.appInfo.pleaseWaitShow();

        // // add orderLine for negative
        // if (!!selectedRow.orderLine.partId) {
        //     const part = (await this.uow.parts.byId(selectedRow.orderLine.partId)) as Part;
        //     const orderLine = this.entitiesService.addOrderLine(
        //         part,
        //         selectedRow.quantity * -1,
        //         null,
        //         selectedRow.orderLine.order,
        //         selectedRow.isReplacement ? 'Replacements' : ''
        //     );
        // } else {
        //     const orderLine = this.entitiesService.addOrderLineForMisc(selectedRow.orderLine, selectedRow.quantity * -1, selectedRow.orderLine.order);
        // }
        await this.utility.deleteOrderLineDetail(selectedRow.id);
        this.entitiesService.createOrderLineDetailNote(selectedRow.id, 'Deleted', this.auth.currentUser.id);
        selectedRow.deleted = new Date();
        selectedRow.orderLine.deleted = new Date();

        // delete the options if there are any
        for (let index = 0; index < selectedRow.optionDetails.length; index++) {
            const element = selectedRow.optionDetails[index];

            if (!element.deleted) {
                // // add orderLine for negative
                // const optionPart = (await this.uow.parts.byId(element.orderLine.partId)) as Part;
                // const optionOrderLine = this.entitiesService.addOrderLine(optionPart, element.quantity * -1, element.orderLine.id, element.orderLine.order);
                await this.utility.deleteOrderLineDetail(element.id);
                this.entitiesService.createOrderLineDetailNote(element.id, 'Deleted', this.auth.currentUser.id);
                element.deleted = new Date();
                element.orderLine.deleted = new Date();
            }
        }

        await this.uow.commit();
        await this.utility.calculateOrderTotal(selectedRow.orderLine.orderId);
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async onExporting(e) {
        SharedModule.onExportingGrid(e.component, this.entitiesService.optionsAndNotesCustomizeCellOverride);
    }

    async kickOffDetail(detailId: number) {
        const detail = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!detail) return;
        this.appInfo.pleaseWaitShow();
        const orderLineDetailIds = [];
        orderLineDetailIds.push(detail.id);
        detail.optionDetails.forEach((option) => {
            orderLineDetailIds.push(option.id);
        });
        await this.utility.processOrderLineDetails(orderLineDetailIds);
        this.appInfo.pleaseWaitHide();
    }

    async printBuildList() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows || selectedRows.length === 0) return;

        const ids = selectedRows.map((x) => {
            return x.id;
        });
        ids.forEach((id) => {
            const schedule = this.uow.assemblySchedules.createEntity() as AssemblySchedule;
            schedule.buildDate = new Date();
            schedule.created = new Date();
            schedule.createdBy = this.auth.currentUser.userName;
            schedule.jigId = 1;
            schedule.orderLineDetailId = id;
        });
        await this.uow.commit();
        await this.reportService.printAssemblySchedule({ ids: ids });
    }

    async setStatusToBuilt() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData() as OrderLineDetail[];
        if (selectedRows.length === 0) return;

        if (selectedRows.length > 1) {
            const success = await confirm(
                `Are you sure you want to update the status for the following?<br/><br/>${this.getDisplayNames(selectedRows)}`,
                this.appInfo.settings.company,
            );
            if (!success) return;
        }

        this.appInfo.pleaseWaitShow();
        const ids = selectedRows.map((d) => d.id);
        const response = await this.utility.areOrderLineDetailsOrOptionsBuilt(ids);
        this.appInfo.pleaseWaitHide();
        if (response.success) {
            const canOverride = this.auth.hasRole('BuildDetail:CanOverrideBuiltCheck');
            const message = `The following details/options have already been marked as Built. ${canOverride ? 'Do you want to continue?' : 'Contact Administrator.'} <br/><br/>${
                response.message
            }`;
            if (canOverride) {
                const success = await confirm(message, this.appInfo.settings.company);
                if (!success) return;
            } else {
                await alert(message, this.appInfo.settings.company);
                return;
            }
        }

        this.appInfo.pleaseWaitShow();
        for (let index = 0; index < selectedRows.length; index++) {
            const row = selectedRows[index];
            await this.utility.updateInventory(row.id);

            this.entitiesService.createOrderLineDetailNote(row.id, `Status Changed to Built`, this.auth.currentUser.id);
            row.statusId = OrderLineStatus.OrderLineStatusOptions.Built;
            // row.built = new Date();  //done in the utility controller
            // row.scheduled = new Date();  //done in the utility controller
            row.optionDetails.forEach((option) => {
                option.statusId = OrderLineStatus.OrderLineStatusOptions.Built;
                // option.built = new Date();  //done in the utility controller
                // option.scheduled = new Date();  //done in the utility controller
            });
        }
        await this.uow.commit();
        await this.refreshNotes();
        this.dataGrid.instance.clearSelection();
        this.appInfo.pleaseWaitHide();
    }

    async toggleHotOrder() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        const markHot = !selectedRows.every((r) => r.hotOrder);
        selectedRows.forEach((r) => (r.hotOrder = markHot));
        await this.uow.commit();
        this.appInfo.pleaseWaitShow();
        await this.dataGrid.instance.refresh();
        this.dataGrid.instance.clearSelection();
        this.appInfo.pleaseWaitHide();
    }

    sortByHotOrder(rowData) {
        return rowData.hotOrder ? 1 : 0;
    }

    async allocateToOrder() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        const result = await confirm(`Are you sure you want to Re-Allocated this detail to a different order?`, `${this.appInfo.settings.company}`);
        if (!result) return;

        //get the order
        const orderLineDetailToFulfill = await this.getOrderToFulfill(selectedRow.id);
        if (!orderLineDetailToFulfill) return;

        this.appInfo.pleaseWaitShow();
        // add orderLine as negative to "remove" it from the Sales Order
        if (!!selectedRow.orderLine.partId) {
            const part = (await this.uow.parts.byId(selectedRow.orderLine.partId)) as Part;
            const orderLine = this.entitiesService.addOrderLine(
                part,
                selectedRow.quantity * -1,
                null,
                selectedRow.orderLine.order,
                selectedRow.isReplacement ? 'Replacements' : '',
            );
        } else {
            const orderLine = this.entitiesService.addOrderLineForMisc(selectedRow.orderLine, selectedRow.quantity * -1, selectedRow.orderLine.order);
        }
        this.entitiesService.createOrderLineDetailNote(selectedRow.id, `Allocated to OrderDetail ${orderLineDetailToFulfill}`, this.auth.currentUser.id);

        //add the Stock's "VIN" as a note for traceability
        this.entitiesService.createOrderLineDetailNote(selectedRow.id, `VIN before allocation: ${selectedRow.vIN}`, this.auth.currentUser.id);
        const allocated = (await this.utility.allocatedStockToOrder(selectedRow.id, orderLineDetailToFulfill)) as any;

        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async getOrderToFulfill(selectedOrderLineDetailId: number): Promise<number> {
        const promise = await new Promise<number | null>(async (resolve) => {
            const modalRef = this.modalService.show(AllocateDialogComponent, {
                class: 'modal-lg',
                ignoreBackdropClick: true,
                keyboard: false,
                initialState: {
                    orderLineDetailId: selectedOrderLineDetailId,
                },
            });
            modalRef.content.result.subscribe(async (result) => {
                if (result.success && result.value) resolve(result.value);
                modalRef.hide();
                resolve(null);
            });
        });
        return promise;
    }

    async markAsNotBuilt() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        if (!selectedRow.built) {
            await alert(`Selected Row is not on Built status`, `${this.appInfo.settings.company}`);
            return;
        }

        const confirmResult = await confirm(
            `This will mark the selected line and all its options as UnBuilt, returning the parts to inventory.  Continue?`,
            `${this.appInfo.settings.company}`,
        );
        if (!confirmResult) return;

        this.appInfo.pleaseWaitShow();

        await this.utility.unBuildOrderLineDetail(selectedRow.id);
        this.entitiesService.createOrderLineDetailNote(selectedRow.id, `Marked as UnBuilt`, this.auth.currentUser.id);
        selectedRow.statusId = OrderLineStatus.OrderLineStatusOptions.New;
        selectedRow.built = null;
        selectedRow.scheduled = null;
        selectedRow.assemblyJigId = null;

        for (let index = 0; index < selectedRow.optionDetails.length; index++) {
            const optionLine = selectedRow.optionDetails[index];

            if (!optionLine.deleted) {
                await this.utility.unBuildOrderLineDetail(optionLine.id);
                this.entitiesService.createOrderLineDetailNote(optionLine.id, `Marked as UnBuilt`, this.auth.currentUser.id);
                optionLine.statusId = OrderLineStatus.OrderLineStatusOptions.New;
                optionLine.built = null;
                optionLine.scheduled = null;
                optionLine.assemblyJigId = null;
            }
        }
        await this.uow.commit();
        await this.refreshNotes();
        this.appInfo.pleaseWaitHide();
        await alert(`Done.  Reminder: OrderLineDetails will need ReKickOff if allocated amounts need to be updated.`, `${this.appInfo.settings.company}`);
    }

    printMsrp(orderLineDetail: OrderLineDetail) {
        if (!orderLineDetail) return;

        this.reportService.openMsrpReport([orderLineDetail.orderLineId]);
    }

    editDealerNote = async (dealer: Dealer) => {
        if (!this.canSeeDealerNotes || !dealer) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Edit Internal Note',
                dialogPrompt: `Edit Internal Note for ${dealer.companyName}`,
                mode: 'textarea',
                dialogValue: dealer.notes,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                dealer.notes = result.value;
                await this.uow.commit();
            }
            modalRef.hide();
        });
    };
}
